// bluaIQ — NBA-10: Proposed Payment Schedule Preview (pure, conservative parser).
//
// PURPOSE: read a completed contract's FREE-TEXT payment terms and, ONLY when the
// terms are simple and unambiguous, produce a read-only PREVIEW of proposed
// milestone rows (label + percent + proposed amount + due trigger). This NEVER
// creates a payment, never infers a calendar date, never calls an endpoint. It is
// a display aid that helps a human decide — the actual payment rows are created
// elsewhere, only after explicit confirmation.
//
// SAFETY PRINCIPLES (per NBA-10 spec):
//   • Conservative by default: if the terms are not clearly parseable into
//     percentages that sum to 100% across recognized milestones, we show NO rows
//     and surface a "review manually" warning instead of guessing.
//   • No AI, no date inference, no payment IDs, no records, no network.
//   • Suspicious-but-finite amounts (e.g. $150,000,000) still preview math (the
//     UI adds a "verify amount/source" warning). Only a truly invalid/unrenderable
//     amount (null/NaN/Infinity/malformed) blocks the math.
//   • Free-text terms ALWAYS warrant a "free text — review" warning even when rows
//     are shown, because the parse is a best-effort convenience, not authority.
//
// Pure + framework-free → fully unit-testable (see nba10ProposedPaymentSchedule
// .smoke.ts) and reusable.

import { coerceAmount } from './money'
// PR-F4G: exact-cent allocation via the PR-F4A money adapter. Proposed amounts
// were previously rounded to WHOLE DOLLARS per row (Math.round(pct/100 * total)),
// which was acceptable for a read-only preview but is not a safe basis for the
// future scheduled-payment create-flow. Lines now allocate in integer cents via
// largest-remainder (Hamilton) so they sum EXACTLY to the contract total.
import { parseMoneyToCents, splitMoneyByPercentages } from '../shared/moneyPrecision'

/** A canonical milestone the conservative parser is willing to propose. */
export type MilestoneKey = 'deposit' | 'completion'

export interface ProposedMilestone {
  /** Canonical milestone bucket. */
  key: MilestoneKey
  /** Whole-or-decimal percent of the contract total (e.g. 50). */
  pct: number
  /** Proposed dollar amount = pct/100 * total — null when the amount is invalid. */
  amount: number | null
  /**
   * Condition that would trigger the payment. A CONDITION, never an inferred
   * calendar date (we never invent dates). 'deposit_start' groups deposit/start
   * (the upfront payment); 'completion' groups completion/final.
   */
  dueTrigger: 'deposit_start' | 'completion'
}

export interface PaymentSchedulePreview {
  /** Proposed rows, in canonical order [deposit, completion]. */
  rows: ProposedMilestone[]
  /** True only when it is SAFE to display the proposed rows. */
  showRows: boolean
  /** True when the contract amount is not a finite, renderable number → blocks math. */
  amountInvalid: boolean
  /** True when the terms string carries any free text at all. */
  hasFreeText: boolean
  /** True when at least one "<n>%" token was found. */
  hasPercentages: boolean
  /** True when classifiable percents were found (mapped to a known milestone). */
  hasClassifiedPercents: boolean
  /** A percent token appeared with no recognizable milestone keyword nearby. */
  unclassifiedPercent: boolean
  /** The same milestone bucket appeared twice with DIFFERENT percentages. */
  conflictingPercent: boolean
  /** Classified milestone percents sum to (approximately) 100. */
  percentsSumTo100: boolean
  /** Ambiguous timing/structural clauses present (retainage, net-X, invoice, etc.). */
  hasAmbiguousClauses: boolean
}

// Keywords that mark the UPFRONT payment (deposit/start are treated as one stage).
const DEPOSIT_RE =
  /\b(deposit|d[eé]p[oó]sito|down\s*payment|anticipo|at\s+start|upon\s+start|al\s+inicio|inicio|at\s+signing|upon\s+signing|al\s+firmar|commencement)\b/i
// Keywords that mark the CLOSING payment (completion/final are treated as one stage).
const COMPLETION_RE =
  /\b(completion|complete|terminaci[oó]n|al\s+terminar|final\s*payment|pago\s*final|final|substantial\s+completion)\b/i

// Timing / structural clauses that make a term too ambiguous to schedule from text.
const AMBIGUOUS_RE =
  /(retainage|retenci[oó]n|within\s+\d+\s+days?|net\s*-?\s*\d+|\d+\s+days?\b|invoice|factura|inspection|inspecci[oó]n|property\s+code|c[oó]digo|legal)/i

const PERCENT_TOKEN_RE = /(\d{1,3}(?:\.\d+)?)\s*%/g

/** Sum tolerance so 33+33+34 style splits are not rejected by float noise. */
const SUM_TOLERANCE = 0.5

/**
 * Conservatively derive a proposed payment schedule preview from free-text terms.
 * Returns rows ONLY when the parse is unambiguous; otherwise returns the flags a
 * UI uses to show the right "review manually" warning. Never throws.
 */
export function deriveProposedSchedule(
  termsInput: unknown,
  totalInput: unknown,
): PaymentSchedulePreview {
  const base: PaymentSchedulePreview = {
    rows: [],
    showRows: false,
    amountInvalid: false,
    hasFreeText: false,
    hasPercentages: false,
    hasClassifiedPercents: false,
    unclassifiedPercent: false,
    conflictingPercent: false,
    percentsSumTo100: false,
    hasAmbiguousClauses: false,
  }

  // Amount hygiene — a non-finite/missing/malformed total blocks preview math.
  const { value: amountValue } = coerceAmount(totalInput)
  base.amountInvalid = amountValue === null

  // Terms hygiene — coerce defensively so an object never becomes "[object Object]".
  const termsText = typeof termsInput === 'string' ? termsInput.trim() : ''
  base.hasFreeText = termsText.length > 0
  if (!base.hasFreeText) return base

  base.hasAmbiguousClauses = AMBIGUOUS_RE.test(termsText)

  // Split into segments so each percent is read with its nearest milestone keyword.
  const segments = termsText.split(/[\n;,]+/).map((s) => s.trim()).filter(Boolean)

  // Group dedup: bucket -> set of percentages seen (to detect conflicts).
  const buckets: Record<MilestoneKey, number[]> = { deposit: [], completion: [] }

  for (const segment of segments) {
    PERCENT_TOKEN_RE.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = PERCENT_TOKEN_RE.exec(segment)) !== null) {
      base.hasPercentages = true
      const pct = Number(m[1])
      if (!Number.isFinite(pct)) continue
      const isDeposit = DEPOSIT_RE.test(segment)
      const isCompletion = COMPLETION_RE.test(segment)
      // A segment that names BOTH or NEITHER is not safely classifiable.
      if (isDeposit === isCompletion) {
        base.unclassifiedPercent = true
        continue
      }
      const key: MilestoneKey = isDeposit ? 'deposit' : 'completion'
      base.hasClassifiedPercents = true
      buckets[key].push(pct)
    }
  }

  // Resolve each bucket to a single percent; differing values in one bucket = conflict.
  const order: MilestoneKey[] = ['deposit', 'completion']
  const resolved: { key: MilestoneKey; pct: number }[] = []
  for (const key of order) {
    const pcts = buckets[key]
    if (pcts.length === 0) continue
    const first = pcts[0]
    if (pcts.some((p) => Math.abs(p - first) > SUM_TOLERANCE)) {
      base.conflictingPercent = true
      continue
    }
    resolved.push({ key, pct: first })
  }

  const sum = resolved.reduce((acc, r) => acc + r.pct, 0)
  base.percentsSumTo100 = resolved.length > 0 && Math.abs(sum - 100) <= SUM_TOLERANCE

  // PR-F4G: EXACT-CENT allocation — the ONLY way a row ever gets an amount.
  // When the classified percents pass the F4A adapter's STRICT sum-to-100 check
  // (tolerance 0.01) and the total parses to safe integer cents, allocate via
  // largest-remainder so the proposed lines sum EXACTLY to the contract total
  // (e.g. $45,000.50 at 50/50 → $22,500.25 each — never 2 × $22,500 with $0.50
  // silently lost). If the exact split does NOT succeed — e.g. 49.9%+49.9%
  // slips through the parser's looser 0.5 sum tolerance but the adapter
  // correctly refuses 99.8% — row amounts are null and rows are NOT shown.
  // There is deliberately NO whole-dollar Math.round fallback anymore: a
  // preview that cannot allocate exactly must say "review manually", never
  // display near-miss amounts that silently lose cents (Codex re-review fix).
  let exactCents: number[] | null = null
  if (!base.amountInvalid && amountValue !== null && resolved.length > 0) {
    const parsedTotal = parseMoneyToCents(amountValue, { roundSubCent: true })
    if (parsedTotal.ok) {
      const split = splitMoneyByPercentages(parsedTotal.cents, resolved.map((r) => r.pct))
      if (split.ok) exactCents = [...split.lines]
    }
  }
  base.rows = resolved.map((r, i) => ({
    key: r.key,
    pct: r.pct,
    amount: exactCents !== null ? exactCents[i] / 100 : null,
    dueTrigger: r.key === 'deposit' ? 'deposit_start' : 'completion',
  }))

  // Safe to show rows ONLY when: ≥2 clean milestones, summing to 100, no
  // unclassified/conflicting percents, a renderable amount, AND the exact-cent
  // allocation succeeded (visible rows are guaranteed to sum to the total).
  base.showRows =
    base.rows.length >= 2 &&
    base.percentsSumTo100 &&
    !base.unclassifiedPercent &&
    !base.conflictingPercent &&
    !base.amountInvalid &&
    exactCents !== null

  return base
}
